import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Divider,
  Table as _Table,
  TableBody as _TableBody,
  TableRow as _TableRow,
  TableCell as _TableCell,
  Tooltip,
} from '@mui/material';
import {
  SwapHoriz as OwnershipIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { anonymizeOwnerName } from '../utils/anonymize';

interface OwnershipEvent {
  effective_date: string;
  ccn_buyer: string;
  ccn_seller: string;
  organizationname_buyer: string;
  doingbusinessasname_buyer: string;
  organizationname_seller: string;
  doingbusinessasname_seller: string;
  match_role: 'buyer' | 'seller' | 'both' | 'unknown';
  matched_id: string;
}

interface ChangeOfOwnershipData {
  has_changes: boolean;
  change_count: number;
  status: string;
  events: OwnershipEvent[];
  current_associate_ids: string[];
  lead_defendant_name?: string;
}

interface SchemeInstance {
  date: string;
  snf_admits_cnt: number;
  is_scheme?: boolean;
  in_case?: boolean;
}

interface ChangeOfOwnershipCardProps {
  ccn: string;
  ownershipData: ChangeOfOwnershipData;
  schemeInstances?: SchemeInstance[];
  demoMode?: boolean;
}

export default function ChangeOfOwnershipCard({ ccn, ownershipData, schemeInstances = [], demoMode = false }: ChangeOfOwnershipCardProps) {
  const [expanded, setExpanded] = useState(false);

  console.log('[ChangeOfOwnershipCard] Full ownership data for CCN:', ccn, ownershipData);

  if (!ownershipData.has_changes) {
    return null; // Don't show card if no ownership changes
  }

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  // Helper function to generate ownership change label based on match_role
  const getOwnershipLabel = (event: OwnershipEvent) => {
    const rawLeadDefendant = ownershipData.lead_defendant_name || 'Defendant';
    const leadDefendant = demoMode ? anonymizeOwnerName(rawLeadDefendant) : rawLeadDefendant;

    console.log('[ChangeOfOwnershipCard] Event:', {
      date: event.effective_date,
      match_role: event.match_role,
      leadDefendant,
      seller: event.organizationname_seller,
      buyer: event.organizationname_buyer
    });

    switch (event.match_role) {
      case 'buyer':
        return `Acquired by ${leadDefendant}`;
      case 'seller':
        return `Sold by ${leadDefendant}`;
      case 'both':
        return `Internal Transfer (${leadDefendant})`;
      case 'unknown':
      default:
        // Fallback to showing org names if match_role is unknown
        const seller = demoMode ? anonymizeOwnerName(event.organizationname_seller) : (event.organizationname_seller || 'Unknown');
        const buyer = demoMode ? anonymizeOwnerName(event.organizationname_buyer) : (event.organizationname_buyer || 'Unknown');
        return `${seller || 'Unknown'} → ${buyer || 'Unknown'}`;
    }
  };

  // Create timeline data combining ownership events and scheme instances
  const timelineStartDate = new Date('2020-01-01');
  const timelineEndDate = new Date('2024-12-31');

  type TimelineEvent = {
    date: string;
    type: 'scheme' | 'ownership';
    label: string;
    color: string;
    snf_admits_cnt?: number;
    event?: OwnershipEvent;
  };

  const timelineEvents: TimelineEvent[] = [
    // Add scheme instances with conditional coloring based on in_case flag
    ...schemeInstances.map((instance): TimelineEvent => ({
      date: instance.date,
      type: 'scheme',
      label: instance.in_case
        ? `Scheme (${instance.snf_admits_cnt} admits)`
        : instance.is_scheme
          ? `Scheme - Not Owner (${instance.snf_admits_cnt} admits)`
          : `Not Scheme (${instance.snf_admits_cnt} admits)`,
      color: instance.in_case ? '#4caf50' : '#d0d0d0', // Green for in_case, light grey for everything else
      snf_admits_cnt: instance.snf_admits_cnt
    })),
    // Add ownership changes as orange markers
    ...ownershipData.events.map((event): TimelineEvent => ({
      date: event.effective_date,
      type: 'ownership',
      label: getOwnershipLabel(event),
      color: '#ff9800',
      event: event
    }))
  ]
    .filter(event => event.date)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return (
    <Card
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'warning.main',
        backgroundColor: 'warning.50'
      }}
    >
      <CardContent sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OwnershipIcon color="warning" />
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
              Ownership Timeline
            </Typography>
            <Chip
              label={`${ownershipData.change_count} ownership change${ownershipData.change_count > 1 ? 's' : ''}`}
              size="small"
              color="warning"
              variant="outlined"
            />
            {schemeInstances.length > 0 && (
              <Chip
                label={`${schemeInstances.length} Bulk SNF admission${schemeInstances.length > 1 ? 's' : ''}`}
                size="small"
                color="success"
                variant="outlined"
              />
            )}
          </Box>
          <IconButton size="small" onClick={handleToggleExpand}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Timeline - always visible */}
        <Box sx={{ mt: 2, mb: 1 }}>
          {timelineEvents.length > 0 ? (
            <Box sx={{ position: 'relative', height: 60, overflow: 'visible' }}>
              {/* Timeline line */}
              <Box sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 30,
                height: 2,
                backgroundColor: 'divider'
              }} />

              {/* Start and End date labels */}
              <Typography variant="caption" sx={{
                position: 'absolute',
                left: 0,
                top: 40,
                color: 'text.secondary'
              }}>
                2020
              </Typography>
              <Typography variant="caption" sx={{
                position: 'absolute',
                right: 0,
                top: 40,
                color: 'text.secondary'
              }}>
                2024
              </Typography>

              {/* Timeline events */}
              {timelineEvents.map((event, idx) => {
                // Calculate position based on date
                const eventDate = new Date(event.date);
                const totalDays = (timelineEndDate.getTime() - timelineStartDate.getTime()) / (1000 * 3600 * 24);
                const eventDays = (eventDate.getTime() - timelineStartDate.getTime()) / (1000 * 3600 * 24);

                // Clamp position between 0% and 100%
                const leftPercent = Math.max(0, Math.min(100, (eventDays / totalDays) * 100));

                return (
                  <Box key={idx}>
                    {/* Timeline dot */}
                    <Box sx={{
                      position: 'absolute',
                      left: `${leftPercent}%`,
                      top: 25,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: event.color,
                      border: '2px solid white',
                      boxShadow: 1,
                      transform: 'translateX(-50%)',
                      zIndex: 2
                    }} />

                    {/* Event tooltip */}
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {new Date(event.date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2">
                            {event.label}
                          </Typography>
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <Box sx={{
                        position: 'absolute',
                        left: `${leftPercent}%`,
                        top: event.type === 'scheme' ? 10 : 5,
                        transform: 'translateX(-50%)',
                        cursor: 'pointer',
                        zIndex: 3
                      }}>
                        <Typography variant="caption" sx={{
                          backgroundColor: event.color,
                          color: 'white',
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 0.5,
                          fontSize: '0.6rem',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap'
                        }}>
                          {event.type === 'scheme' ? 'S' : 'O'}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
              No timeline events available
            </Typography>
          )}

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#4caf50'
              }} />
              <Typography variant="caption">In Case</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#d0d0d0'
              }} />
              <Typography variant="caption">Not In Case</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#ff9800'
              }} />
              <Typography variant="caption">Ownership Change</Typography>
            </Box>
          </Box>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
              Event Details
            </Typography>

            {/* Ownership Events */}
            {ownershipData.events.map((event, index) => (
              <Box key={`ownership-${index}`} sx={{ mb: 2, p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'orange.300' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#ff9800'
                  }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Ownership Change - {new Date(event.effective_date).toLocaleDateString()}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {getOwnershipLabel(event)}
                </Typography>
              </Box>
            ))}

            {/* Scheme Events */}
            {schemeInstances.map((instance, index) => (
              <Box key={`scheme-${index}`} sx={{ mb: 2, p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid', borderColor: instance.in_case ? 'green.300' : 'grey.300' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: instance.in_case ? '#4caf50' : '#d0d0d0'
                  }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Bulk SNF Admission - {new Date(instance.date).toLocaleDateString()}
                    {instance.in_case ? (
                      <Chip
                        label="SCHEME"
                        size="small"
                        color="success"
                        variant="filled"
                        sx={{ ml: 1, fontSize: '0.6rem', height: 16 }}
                      />
                    ) : instance.is_scheme ? (
                      <Chip
                        label="SCHEME (NOT OWNER)"
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ ml: 1, fontSize: '0.6rem', height: 16 }}
                      />
                    ) : null}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {instance.snf_admits_cnt} SNF admissions
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}